const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.classnx.com";

const ADMIN_BASE = "/api/admin";
const SUPER_ADMIN_BASE = "/api/super-admin";
const TEACHER_BASE = "/api/teacher";
const ADMISSION_BASE = "/api/admission";
const SUBSCRIPTION_V2_BASE = "/api/v2/subscription";
const SUBSCRIPTION_BASE = "/api/subscription";
const PAYMENTS_BASE = "/api/payments";
const PUBLIC_BASE = "/api/public";
const PRINCIPAL_BASE = "/api/principal";

const encodePathSegment = (value) => encodeURIComponent(String(value ?? ""));
const encodeQueryValue = (value) => encodeURIComponent(String(value ?? ""));
const byId = (base, id) => `${base}/${encodePathSegment(id)}`;

export const API_BASES = {
  app: API_BASE_URL,
};

export const API_PATHS = {
  branding: {
    schoolLogo: () => `${ADMIN_BASE}/school/logo`,
    homeSlides: () => "/api/v2/branding/home-slides",
  },
  auth: {
    login: "/api/v2/auth/login",
    logout: "/api/v2/auth/logout",
    session: "/api/v2/auth/session",
    csrf: "/api/v2/auth/csrf",
    forgotPassword: "/api/v2/auth/forgot-password",
    resetPassword: "/api/v2/auth/reset-password",
    employeeActivationSetPassword: "/api/v2/auth/employee-activation/set-password",
    presenceHeartbeat: "/api/v2/auth/presence/heartbeat",
    refresh: "/api/v2/auth/refresh",
  },
  admin: {
    profile: () => "/admin/profilev2",
    users: () => `${ADMIN_BASE}/users`,
    createUser: () => `${ADMIN_BASE}/users`,
    userStatus: (userId) => `${ADMIN_BASE}/users/${encodePathSegment(userId)}/status`,
    teachers: () => `${ADMIN_BASE}/teachers`,
    teacherById: (teacherId) => byId(`${ADMIN_BASE}/teachers`, teacherId),
    resendTeacherInvitation: (teacherId) => `${byId(`${ADMIN_BASE}/teachers`, teacherId)}/resend-invitation`,
    teacherDocuments: (teacherId) => `${byId(`${ADMIN_BASE}/teachers`, teacherId)}/documents`,
    students: () => `${ADMIN_BASE}/students`,
    importModule: (moduleKey) => `${ADMIN_BASE}/import/${encodePathSegment(moduleKey)}`,
    studentById: (studentId) => byId(`${ADMIN_BASE}/students`, studentId),
    classes: () => `${ADMIN_BASE}/classes`,
    classById: (classId) => byId(`${ADMIN_BASE}/classes`, classId),
    subjects: () => `${ADMIN_BASE}/subjects`,
    subjectById: (subjectId) => byId(`${ADMIN_BASE}/subjects`, subjectId),
    timetable: () => `${ADMIN_BASE}/timetable`,
    timetableById: (slotId) => byId(`${ADMIN_BASE}/timetable`, slotId),
    exams: () => `${ADMIN_BASE}/exams`,
    examById: (examId) => byId(`${ADMIN_BASE}/exams`, examId),
    fees: () => `${ADMIN_BASE}/fees`,
    feeById: (feeId) => byId(`${ADMIN_BASE}/fees`, feeId),
    notifications: () => `${ADMIN_BASE}/notifications`,
    notificationById: (notificationId) => byId(`${ADMIN_BASE}/notifications`, notificationId),
    permissions: () => `${ADMIN_BASE}/permissions`,
    permissionById: (permissionId) => byId(`${ADMIN_BASE}/permissions`, permissionId),
    parents: () => `${ADMIN_BASE}/parents`,
    parentById: (parentId) => byId(`${ADMIN_BASE}/parents`, parentId),
    dashboardMetrics: () => `${ADMIN_BASE}/dashboard/metrics`,
    studentAdmissions: () => `${ADMIN_BASE}/students/admissions`,
    studentAdmissionById: (studentId) => `${ADMIN_BASE}/students/${encodePathSegment(studentId)}/admission`,
    studentFees: () => `${ADMIN_BASE}/students/fees`,
    studentFeesById: (studentId) => `${ADMIN_BASE}/students/${encodePathSegment(studentId)}/fees`,
    studentParentContacts: () => `${ADMIN_BASE}/students/parent-contacts`,
    studentParentContactById: (studentId) => `${ADMIN_BASE}/students/${encodePathSegment(studentId)}/parent-contact`,
    studentActivityLog: () => `${ADMIN_BASE}/students/activity-log`,
    studentActivityLogById: (studentId) => `${ADMIN_BASE}/students/${encodePathSegment(studentId)}/activity-log`,
    gallery: () => `${ADMIN_BASE}/gallery`,
    galleryById: (id) => `${ADMIN_BASE}/gallery/${encodePathSegment(id)}`,
    assessments: () => `${ADMIN_BASE}/assessments`,
    schoolLogo: () => `${ADMIN_BASE}/school/logo`,
    homeSlides: () => `${ADMIN_BASE}/school/home-slides`,
    homeSlideById: (slideId) => byId(`${ADMIN_BASE}/school/home-slides`, slideId),
    assessmentById: (assessmentId) => `${ADMIN_BASE}/assessments/${encodePathSegment(assessmentId)}`,
    publishAssessment: (assessmentId) => `${ADMIN_BASE}/assessments/${encodePathSegment(assessmentId)}/publish`,
    unpublishAssessment: (assessmentId) => `${ADMIN_BASE}/assessments/${encodePathSegment(assessmentId)}/unpublish`,
    assessmentResults: (assessmentId) => `${ADMIN_BASE}/assessments/${encodePathSegment(assessmentId)}/results`,
  },
  superAdmin: {
    loginAds: () => `${SUPER_ADMIN_BASE}/login-ads`,
    loginAdMedia: () => `${SUPER_ADMIN_BASE}/login-ads/media`,
    loginAdById: (adId) => byId(`${SUPER_ADMIN_BASE}/login-ads`, adId),
    loginAdStatus: (adId) => `${byId(`${SUPER_ADMIN_BASE}/login-ads`, adId)}/status`,
    loginAdsOrder: () => `${SUPER_ADMIN_BASE}/login-ads/order`,
    overview: () => `${SUPER_ADMIN_BASE}/overview`,
    objectStorage: () => `${SUPER_ADMIN_BASE}/object-storage`,
    moments: () => `${SUPER_ADMIN_BASE}/moments`,
    momentVisibility: (momentId) => `${byId(`${SUPER_ADMIN_BASE}/moments`, momentId)}/visibility`,
    momentById: (momentId) => byId(`${SUPER_ADMIN_BASE}/moments`, momentId),
    devActivities: () => `${SUPER_ADMIN_BASE}/dev-activities`,
    devActivityById: (activityId) => byId(`${SUPER_ADMIN_BASE}/dev-activities`, activityId),
    tenants: () => `${SUPER_ADMIN_BASE}/tenants`,
    viewAllTenants: () => "/platform/viewAllTenants",
    tenantById: (tenantId) => byId(`${SUPER_ADMIN_BASE}/tenants`, tenantId),
    tenantStatus: (tenantId) => `${SUPER_ADMIN_BASE}/tenants/${encodePathSegment(tenantId)}/status`,
    tenantSubscription: (tenantId) => `${SUPER_ADMIN_BASE}/tenants/${encodePathSegment(tenantId)}/subscription`,
    tenantSubscriptionReminder: (tenantId) => `${SUPER_ADMIN_BASE}/tenants/${encodePathSegment(tenantId)}/subscription/reminder`,
    subscriptionFinancials: () => `${SUPER_ADMIN_BASE}/subscriptions/financials`,
    tenantSubscriptionFinancials: (tenantId) => `${SUPER_ADMIN_BASE}/tenants/${encodePathSegment(tenantId)}/subscription/financials`,
    tenantFeatureRequests: (status = "") => `${SUPER_ADMIN_BASE}/tenant-feature-requests${status ? `?status=${encodeQueryValue(status)}` : ""}`,
    learningResourceRequests: () => `${SUPER_ADMIN_BASE}/learning-resource-requests`,
    learningResourceRequestReview: (requestId) => `${SUPER_ADMIN_BASE}/learning-resource-requests/${encodePathSegment(requestId)}/review`,
    learningResources: () => `${SUPER_ADMIN_BASE}/learning-resources`,
    tenantFeatureRequestById: (requestId) => byId(`${SUPER_ADMIN_BASE}/tenant-feature-requests`, requestId),
    tenantModuleTabs: (tenantId) => `${SUPER_ADMIN_BASE}/tenants/${encodePathSegment(tenantId)}/module-tabs`,
    subscriptions: () => `${SUPER_ADMIN_BASE}/subscriptions`,
    trialRequests: () => `${SUBSCRIPTION_BASE}/extension-requests`,
    approveTrialRequest: (id) => `${SUBSCRIPTION_BASE}/approve-extension?requestId=${encodeQueryValue(id)}`,
    rejectTrialRequest: (id) => `${SUBSCRIPTION_BASE}/reject-extension?requestId=${encodeQueryValue(id)}`,
    cancellationRequests: () => `${SUBSCRIPTION_BASE}/cancellation-requests`,
    approveCancellationRequest: (id) => `${SUBSCRIPTION_BASE}/cancel/approve?requestId=${encodeQueryValue(id)}`,
    rejectCancellationRequest: (id) => `${SUBSCRIPTION_BASE}/cancel/reject?requestId=${encodeQueryValue(id)}`,
    cancelDirect: (tenantId) => `${SUBSCRIPTION_BASE}/cancel/direct?tenantId=${encodeQueryValue(tenantId)}`,
    extendDirect: (tenantId, days = 7) => `${SUBSCRIPTION_BASE}/extend-direct?tenantId=${encodeQueryValue(tenantId)}&days=${encodeQueryValue(days)}`,
    discountConfig: () => `${SUPER_ADMIN_BASE}/discounts/config`,
    createCoupon: () => `${SUPER_ADMIN_BASE}/discounts/coupons`,
    couponStatus: (couponId) => `${SUPER_ADMIN_BASE}/discounts/coupons/${encodePathSegment(couponId)}/status`,
    referralConfig: () => `${SUPER_ADMIN_BASE}/discounts/referral`,
    visitorAnalytics: (days = 7) => `${SUPER_ADMIN_BASE}/visitor-analytics?days=${encodeQueryValue(days)}`,
    visitorAnalyticsCountries: (days = 7) => `${SUPER_ADMIN_BASE}/visitor-analytics/countries?days=${encodeQueryValue(days)}`,
    visitorAnalyticsCountryDiagnostics: () => `${SUPER_ADMIN_BASE}/visitor-analytics/countries/diagnostics`,
    visitorAnalyticsStream: (days = 7) => `${SUPER_ADMIN_BASE}/visitor-analytics/stream?days=${encodeQueryValue(days)}`,
    tenantBrandingChangeRequests: () => `${SUPER_ADMIN_BASE}/tenant-branding-change-requests`,
    approveTenantBrandingChangeRequest: (requestId) => `${byId(`${SUPER_ADMIN_BASE}/tenant-branding-change-requests`, requestId)}/approve`,
    rejectTenantBrandingChangeRequest: (requestId) => `${byId(`${SUPER_ADMIN_BASE}/tenant-branding-change-requests`, requestId)}/reject`,
    employees: () => `${SUPER_ADMIN_BASE}/employees`,
    employeeById: (employeeId) => byId(`${SUPER_ADMIN_BASE}/employees`, employeeId),
    employeeStatus: (employeeId) => `${byId(`${SUPER_ADMIN_BASE}/employees`, employeeId)}/status`,
    resendEmployeeInvitation: (employeeId) => `${byId(`${SUPER_ADMIN_BASE}/employees`, employeeId)}/resend-invitation`,
    tenantPrincipals: (tenantId) => `${SUPER_ADMIN_BASE}/tenants/${encodePathSegment(tenantId)}/principals`,
    operations: {
      overview: () => `${SUPER_ADMIN_BASE}/operations/overview`,
      control: (controlId) => `${SUPER_ADMIN_BASE}/operations/controls/${encodePathSegment(controlId)}`,
      action: (actionId) => `${SUPER_ADMIN_BASE}/operations/actions/${encodePathSegment(actionId)}`,
    },
  },
  tenant: {
    moduleFeatures: () => "/api/tenant/module-features",
    featureRequests: () => "/api/tenant-feature-requests",
    learningResourceRequests: () => "/api/tenant/learning-resource-requests",
  },
  principal: {
    dashboard: () => `${PRINCIPAL_BASE}/dashboard`,
    students: () => `${PRINCIPAL_BASE}/students`,
    teachers: ({ search = "", page = 0, size = 25, sort = "name" } = {}) => `${PRINCIPAL_BASE}/teachers?search=${encodeQueryValue(search)}&page=${encodeQueryValue(page)}&size=${encodeQueryValue(size)}&sort=${encodeQueryValue(sort)}`,
    classes: () => `${PRINCIPAL_BASE}/classes`,
    admissions: () => `${PRINCIPAL_BASE}/admissions`,
    fees: () => `${PRINCIPAL_BASE}/fees`,
    payroll: () => `${PRINCIPAL_BASE}/payroll`,
    expenses: () => `${PRINCIPAL_BASE}/expenses`,
  },
  schoolLeads: {
    create: () => "/api/public/school-demo-requests",
    list: () => `${SUPER_ADMIN_BASE}/school-demo-requests`,
    byId: (leadId) => byId(`${SUPER_ADMIN_BASE}/school-demo-requests`, leadId),
  },
  platform: {
    moduleTabs: () => "/api/platform/module-tabs",
  },
  student: {
    profile: "/students/profile",
    createStudent: () => "/students/createStudent",
    importStudents: () => "/students/import",
    viewAll: () => "/students/viewAllStudents",
    viewAllStudents: () => "/students/viewAllStudents",
    students: () => "/students/viewAllStudents",
    studentById: (studentId) => `/students/${encodePathSegment(studentId)}`,
    classmates: () => `/api/student/classmates`,
    classmateProfile: (studentId) => `/api/student/classmates/${encodePathSegment(studentId)}/profile`,
    onlineClasses: () => `/api/v2/classroom/rooms`,
    // The classroom API resolves this path parameter with roomCode, not the
    // room database UUID.
    joinOnlineClass: (roomCode) => `/api/v2/classroom/rooms/${encodePathSegment(roomCode)}/join`,
    assessments: () => `/api/student/assessments`,
    startAssessment: (assessmentId) => `/api/student/assessments/${encodePathSegment(assessmentId)}/start`,
    submitAssessment: (assessmentId) => `/api/student/assessments/${encodePathSegment(assessmentId)}/submit`,
    learningResources: () => "/api/student/learning-resources",
    learningResourceAccess: (resourceId) => `/api/student/learning-resources/${encodePathSegment(resourceId)}/access`,
  },
  teacher: {
    profile: "/teachers/profile",
    viewAllStudents: () => "/students/viewAllStudents",
    classes: () => `${TEACHER_BASE}/classes`,
    classStudentsAll: `/students/viewAllStudents`,
    classStudents: (classId) => `${TEACHER_BASE}/classes/${encodePathSegment(classId)}/students`,
    studentsGroupedByClass: () => "/students/viewAllStudentsGroupedByClass",
    classesDropdown: () => "/students/classes/dropdown",
    classSchedule: (classId) => `${TEACHER_BASE}/classes/${encodePathSegment(classId)}/schedule`,
    classAttendanceToday: (classId) => `${TEACHER_BASE}/classes/${encodePathSegment(classId)}/attendance/today`,
    markAttendance: () => `${TEACHER_BASE}/attendance/mark`,
    markAllAttendance: () => `${TEACHER_BASE}/attendance/mark-all`,
    requestAttendanceCorrection: (attendanceId) => `${TEACHER_BASE}/attendance/${encodePathSegment(attendanceId)}/correction-request`,
    applyAttendanceCorrection: (attendanceId) => `${TEACHER_BASE}/attendance/${encodePathSegment(attendanceId)}/correction`,
    updateStudent: (studentId) => `${TEACHER_BASE}/students/${encodePathSegment(studentId)}`,
    saveReportCard: (studentId) => `${TEACHER_BASE}/students/${encodePathSegment(studentId)}/reportcard`,
    onlineClasses: () => `/api/v2/classroom/rooms`,
    onlineClassById: (sessionId) => `${TEACHER_BASE}/online-classes/${encodePathSegment(sessionId)}`,
    startOnlineClass: (sessionId) => `/api/v2/classroom/rooms/${encodePathSegment(sessionId)}/start`,
    // Classroom rooms are ended by public room code, not the database UUID.
    endOnlineClass: (roomCode) => `/api/v2/classroom/rooms/${encodePathSegment(roomCode)}`,
    assessments: () => `${TEACHER_BASE}/assessments`,
    assessmentById: (assessmentId) => `${TEACHER_BASE}/assessments/${encodePathSegment(assessmentId)}`,
    publishAssessment: (assessmentId) => `${TEACHER_BASE}/assessments/${encodePathSegment(assessmentId)}/publish`,
    unpublishAssessment: (assessmentId) => `${TEACHER_BASE}/assessments/${encodePathSegment(assessmentId)}/unpublish`,
    assessmentResults: (assessmentId) => `${TEACHER_BASE}/assessments/${encodePathSegment(assessmentId)}/results`,
  },
  admission: {
    adminDashboard: `${ADMISSION_BASE}/admin/dashboard`,
    save: `${ADMISSION_BASE}/create-admission`,
    pay: `${ADMISSION_BASE}/pay`,
    verify: `${ADMISSION_BASE}/verify`,
    myForm: `${ADMISSION_BASE}/myform`,
  },
  subscription: {
    plans: `${SUBSCRIPTION_V2_BASE}/plans`,
    status: `${SUBSCRIPTION_V2_BASE}/status`,
    pay: `${SUBSCRIPTION_V2_BASE}/pay`,
    requestExtension: `${SUBSCRIPTION_BASE}/request-extension`,
    requestCancellation: `${SUBSCRIPTION_BASE}/cancel/request`,
    extensionRequests: `${SUBSCRIPTION_BASE}/extension-requests`,
    cancellationRequests: `${SUBSCRIPTION_BASE}/cancellation-requests`,
    approveExtension: (requestId) => `${SUBSCRIPTION_BASE}/approve-extension?requestId=${encodeQueryValue(requestId)}`,
    rejectExtension: (requestId) => `${SUBSCRIPTION_BASE}/reject-extension?requestId=${encodeQueryValue(requestId)}`,
    extendDirect: (tenantId, days) => `${SUBSCRIPTION_BASE}/extend-direct?tenantId=${encodeQueryValue(tenantId)}&days=${encodeQueryValue(days)}`,
    payments: `${SUBSCRIPTION_BASE}/payments`,
  },
  payments: {
    createOrder: `${PAYMENTS_BASE}/create-order`,
    verifyPayment: `${SUBSCRIPTION_V2_BASE}/verify`,
    webhook: "/api/v2/payments/webhook",
  },
  usage: {
    studentStats: "/api/usage/student-stats/me",
    teacherStudents: "/api/usage/teacher-students",
    logSession: "/api/usage/log-session",
  },
  chatbot: {
    ask: "/api/chatbot/ask",
  },
  public: {
    tenantResolve: () => `${PUBLIC_BASE}/tenant/resolve`,
    tenantSubdomainResolve: (subdomain) => `${PUBLIC_BASE}/tenant/subdomain/resolve?subdomain=${encodeQueryValue(subdomain)}`,
    loginAds: () => `${PUBLIC_BASE}/login-ads`,
    loginAdClick: (adId) => `${PUBLIC_BASE}/login-ads/${encodePathSegment(adId)}/click`,
    schools: () => `${PUBLIC_BASE}/schools`,
    feedback: () => `${PUBLIC_BASE}/feedback`,
    visitorHeartbeat: () => `${PUBLIC_BASE}/visitors/heartbeat`,
    visitorStats: () => `${PUBLIC_BASE}/visitors/today`,
  },
  moments: {
    list: () => `/api/moments`,
    create: () => `/api/moments/create`,
    like: (id) => `/api/moments/${encodePathSegment(id)}/like`,
    unlike: (id) => `/api/moments/${encodePathSegment(id)}/unlike`,
    comments: (id) => `/api/moments/${encodePathSegment(id)}/comments`,
    commentLike: (momentId, commentId) => `/api/moments/${encodePathSegment(momentId)}/comments/${encodePathSegment(commentId)}/like`,
    commentUnlike: (momentId, commentId) => `/api/moments/${encodePathSegment(momentId)}/comments/${encodePathSegment(commentId)}/unlike`,
    commentReplies: (momentId, commentId) => `/api/moments/${encodePathSegment(momentId)}/comments/${encodePathSegment(commentId)}/replies`,
    deletePost: (id) => `/api/moments/${encodePathSegment(id)}`,
  },
};

export default API_PATHS;
